import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { faEye, faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerModule } from 'ngx-spinner';
import { FormMode } from 'src/app/controls/common/form-mode';
import { ContextOfUse } from 'src/app/models/RestApiCaller/Enums/context-of-use';
import { IRestApiConfiguration } from 'src/app/models/RestApiCaller/Interfaces/rest-api-configuration.interface';
import { RestApiConfiguration } from 'src/app/models/RestApiCaller/configuration/rest-api-configuration';
import { RestApiEventConfiguration } from 'src/app/models/RestApiCaller/configuration/rest-api-event-configuration';
import { ApiConfigurationWizardStep } from 'src/app/models/enums/api-configuration-wizard-step';
import { ApiConfigurationDetailContext } from '../api-configuration-detail-context';
import { ApiConfigurationEventListComponent } from './api-configuration-event-list/api-configuration-event-list.component';
import { ApiConfigurationEventComponent } from './api-configuration-event/api-configuration-event.component';
import { ApiConfigurationGeneralComponent } from './api-configuration-general/api-configuration-general.component';
import { ApiConfigurationRequestComponent } from './api-configuration-request/api-configuration-request.component';
import { ApiConfigurationResponseComponent } from './api-configuration-response/api-configuration-response.component';
import { ApiConfigurationState } from './models/api-configuration-state';

@Component({
    selector: 'app-api-configuration-wizard',
    imports: [
        CommonModule,
        NgxSpinnerModule,
        ApiConfigurationGeneralComponent,
        ApiConfigurationRequestComponent,
        ApiConfigurationResponseComponent,
        ApiConfigurationEventComponent,
        ApiConfigurationEventListComponent,
        MatIconModule,
        MatButtonModule,
        MatTabsModule,
    ],
    templateUrl: './api-configuration-wizard.component.html',
    styleUrl: './api-configuration-wizard.component.scss',
})
export class ApiConfigurationWizardComponent {
    protected readonly apiConfigurationState = inject(ApiConfigurationState);

    /**
     * Liste des enums utilisés dans le template.
     */
    public ApiConfigurationWizardStep = ApiConfigurationWizardStep;
    public FormMode = FormMode;
    public ContextOfUse = ContextOfUse;

    /**
     * Liste des icones utilisés dans le template.
     */
    public faEdit = faPenToSquare;
    public faReadOnly = faEye;

    /**
     * Contexte de la configuration de l'API.
     */
    @Input() context: ApiConfigurationDetailContext;

    /**
     * Emis lorsque la configuration de l'API est mise à jour.
     */
    @Output() apiConfigurationUpdated: EventEmitter<IRestApiConfiguration> = new EventEmitter();

    /**
     * Titre affiché pour le wizard.
     */
    public wizardTitle: string;

    /**
     * Liste des menus du wizard associés à leur libellé.
     */
    public wizardMenuLabels = new Map<ApiConfigurationWizardStep, string>([
        [ApiConfigurationWizardStep.General, 'Général'],
        [ApiConfigurationWizardStep.Request, 'Requête'],
        [ApiConfigurationWizardStep.Response, 'Réponse'],
        [ApiConfigurationWizardStep.EventsList, 'Évènements'],
        [ApiConfigurationWizardStep.Event, 'Évènement'],
    ]);

    /**
     * Etape sélectionné du wizard.
     */
    public currentApiConfigurationWizardStep = ApiConfigurationWizardStep.EventsList;

    /**
     * Configuration d'évènement selectionnée.
     */
    public currentEventConfiguration: RestApiEventConfiguration;

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Enregistrer'
     * @param apiConfiguration Instance de la configuration d'API modifié
     */
    public onApiConfiguartionSave(apiConfiguration: RestApiConfiguration) {
        this.apiConfigurationUpdated.emit(apiConfiguration);
    }

    /**
     * Appellé lorsque l'utilisateur selectionne une configuration d'évènement de la liste des configurations d'évènements.
     * @param eventConfiguration Configuration d'évènement selectionnée.
     */
    public onEventSelected(eventConfiguration: RestApiEventConfiguration) {
        this.currentEventConfiguration = eventConfiguration;
        this.currentApiConfigurationWizardStep = ApiConfigurationWizardStep.EventsList;
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Visualiser' dans le menu contextuel de la liste des configurations d'évènements.
     * @param eventConfiguration Configuration d'évènement selectionnée.
     */
    public onEventDisplayed(eventConfiguration: RestApiEventConfiguration) {
        this.currentEventConfiguration = eventConfiguration;
        this.currentApiConfigurationWizardStep = ApiConfigurationWizardStep.Event;
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel de la liste des configurations d'évènements.
     * @param eventConfiguration Configuration d'évènement selectionnée.
     */
    public onEventEdited(eventConfiguration: RestApiEventConfiguration) {
        this.currentEventConfiguration = eventConfiguration;
        this.currentApiConfigurationWizardStep = ApiConfigurationWizardStep.Event;
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Créer'.
     */
    public onEventCreated() {
        this.currentEventConfiguration = new RestApiEventConfiguration();
        this.currentApiConfigurationWizardStep = ApiConfigurationWizardStep.Event;
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Dupliquer' dans le menu contextuel de la liste des configurations d'évènements.
     * @param eventConfiguration Configuration d'évènement selectionnée.
     */
    public onEventDuplicated(eventConfiguration: RestApiEventConfiguration) {
        this.currentEventConfiguration = RestApiEventConfiguration.clone(eventConfiguration);
        this.currentEventConfiguration.id = null;
        this.currentApiConfigurationWizardStep = ApiConfigurationWizardStep.Event;
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Supprimer' dans le menu contextuel de la liste des configurations d'évènements.
     * @param eventConfiguration Configuration d'évènement selectionnée.
     */
    public onEventDeleted(eventConfiguration: RestApiEventConfiguration) {
        console.log("Reception évènement suppression d'évènement : ", eventConfiguration);
        let apiConfiguration = this.apiConfigurationState.$apiConfiguration().createCopy();

        const deletedEventConfigurationIndex = apiConfiguration.eventConfigurations.findIndex((ev) => ev.id === eventConfiguration.id);
        apiConfiguration.eventConfigurations.splice(deletedEventConfigurationIndex, 1);
        console.log('Modification du signal...');
        this.apiConfigurationState.$apiConfiguration.set(apiConfiguration);
        console.log('Signal modifié !');
    }

    /**
     * Appellé lorsque l'utilisateur change l'ordre des configurations d'évènements.
     * @param sortedEventConfigurations Configurations d'évènement triés.
     */
    public onEventsSorted(sortedEventConfigurations: RestApiEventConfiguration[]) {
        let apiConfiguration = this.apiConfigurationState.$apiConfiguration().createCopy();
        apiConfiguration.eventConfigurations = sortedEventConfigurations;
        this.apiConfigurationState.$apiConfiguration.set(apiConfiguration);
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Annuler' du formulaire d'un évènement'.
     */
    public onEventCanceled() {
        this.currentApiConfigurationWizardStep = ApiConfigurationWizardStep.EventsList;
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Sauvegarder' du formulaire d'un évènement'.
     */
    public onEventSaved() {
        this.currentApiConfigurationWizardStep = ApiConfigurationWizardStep.EventsList;
    }

    public onTabChange(event: MatTabChangeEvent) {
        if (event.index === 3) {
            this.currentApiConfigurationWizardStep = ApiConfigurationWizardStep.EventsList;
        }
    }
}
